<template>
  <div>
    <div class="card border-0 shadow-sm" style="border-radius: 14px; background: #ffffff; border: 1.5px solid #e2e8f0 !important; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);">
      <!-- Standard Vertex Header -->
      <div class="card-header bg-white border-0 pt-3 pb-2 px-3 d-flex justify-content-between align-items-start align-items-sm-center flex-wrap gap-2" style="border-bottom: 1px solid #f1f5f9 !important;">
        <div class="d-flex align-items-center gap-2">
          <div class="widget-icon-pill" style="background-color: #e0f7fa; color: #00a0c1; width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 0.95rem; flex-shrink: 0;">
            <i class="fas fa-chart-pie"></i>
          </div>
          <div>
            <h6 class="mb-0 fw-bold" style="color: #1e293b; font-size: 0.875rem;">Training Summary Status</h6>
            <small class="text-muted" style="color: #64748b; font-size: 0.72rem;">Overview of user completion & progress</small>
          </div>
        </div>
        <div class="widget-controls d-flex align-items-center gap-2">
          <label class="mandatory-toggle-container m-0" v-tooltip.top="'Filter compliance metrics by mandatory programs only'">
            <input
              class="mandatory-toggle-input"
              type="checkbox"
              role="switch"
              id="mandatorySwitchPie"
              v-model="mandatoryOnly"
              @change="onMandatoryChange"
            />
            <span class="mandatory-toggle-track">
              <span class="mandatory-toggle-thumb"></span>
            </span>
            <span class="ms-2 mandatory-toggle-label">
              Mandatory
            </span>
          </label>
        </div>
      </div>

      <div class="card-body p-3">
        <!-- Content row with Legend on left and Donut on right -->
        <div class="row align-items-center py-2" style="min-height: 180px;">
          <div class="col text-center mx-auto" v-if="!pieReady">
            <img src="@/assets/img/loading.svg" class="d-none d-sm-inline mx-auto" alt="">
            <img src="@/assets/img/loading-mobile.svg" class="d-inline d-sm-none mb-3" alt="">
          </div>
          <div class="col-12" v-else>
            <div class="d-flex flex-row flex-wrap align-items-center justify-content-around gap-3 px-2">
              <!-- Custom Figma Legend -->
              <div class="summary-legend d-flex flex-column justify-content-center">
                <div
                  v-for="item in summaryItems"
                  :key="item.name"
                  class="legend-group mb-3"
                  :class="{ 'legend-active': hoveredItem === item.name }"
                  @mouseenter="hoveredItem = item.name"
                  @mouseleave="hoveredItem = null"
                >
                  <div class="d-flex align-items-center">
                    <span class="legend-dot me-2" :style="{ backgroundColor: item.color }"></span>
                    <span class="legend-title">{{ item.name }}</span>
                  </div>
                  <div class="legend-percentage ps-3 ms-1">
                    {{ item.percentage.toFixed(1) }}%
                  </div>
                </div>
              </div>

              <!-- Sleek Figma Donut SVG Chart with Variable Thickness & Rounded Ends -->
              <div class="donut-chart-container position-relative d-flex justify-content-center align-items-center">
                <svg class="donut-svg" viewBox="0 0 160 160" width="160" height="160">
                  <!-- Empty placeholder ring if 0 total -->
                  <circle
                    v-if="totalRecords === 0"
                    cx="80"
                    cy="80"
                    r="58"
                    fill="none"
                    stroke="#E2E8F0"
                    stroke-width="10"
                  />
                  <!-- Single slice 100% full circle -->
                  <circle
                    v-else-if="singleActiveSlice"
                    cx="80"
                    cy="80"
                    r="58"
                    fill="none"
                    :stroke="singleActiveSlice.color"
                    stroke-width="11"
                    :style="{
                      opacity: hoveredItem && hoveredItem !== singleActiveSlice.name ? 0.35 : 1,
                      transition: 'opacity 0.25s ease'
                    }"
                    @mouseenter="hoveredItem = singleActiveSlice.name"
                    @mouseleave="hoveredItem = null"
                  >
                    <title>{{ singleActiveSlice.name }}: {{ singleActiveSlice.value }} ({{ singleActiveSlice.percentage.toFixed(1) }}%)</title>
                  </circle>
                  <!-- Multi-slice rounded arcs matching Figma with variable thickness and generous gaps -->
                  <g v-else>
                    <path
                      v-for="arc in donutArcs"
                      :key="arc.name"
                      :d="arc.d"
                      :stroke="arc.color"
                      :stroke-width="hoveredItem === arc.name ? arc.strokeWidth + 2 : arc.strokeWidth"
                      stroke-linecap="round"
                      fill="none"
                      class="donut-arc-path"
                      :style="{
                        opacity: hoveredItem && hoveredItem !== arc.name ? 0.35 : 1,
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                      }"
                      @mouseenter="hoveredItem = arc.name"
                      @mouseleave="hoveredItem = null"
                    >
                      <title>{{ arc.name }}: {{ arc.value }} ({{ arc.percentage.toFixed(1) }}%)</title>
                    </path>
                  </g>
                </svg>

                <!-- Hover Tooltip Badge -->
                <transition name="fade">
                  <div
                    v-if="hoveredArc && totalRecords > 0"
                    class="donut-hover-tooltip"
                  >
                    <span class="tooltip-dot" :style="{ backgroundColor: hoveredArc.color }"></span>
                    <span class="tooltip-text">{{ hoveredArc.name }}: <strong>{{ hoveredArc.value }} ({{ hoveredArc.percentage.toFixed(1) }}%)</strong></span>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <!-- Card Footer with Download Button -->
        <div class="mt-3 pt-3 border-top d-flex align-items-center" style="border-color: #f1f5f9 !important;">
          <button
            :disabled="downloading"
            type="button"
            class="btn-download-pill"
            v-tooltip.top="'Download Detailed Employee Report (.xlsx)'"
            @click="downloadEmployeeSummary"
          >
            <div v-if="downloading" class="spinner-border text-primary me-1" role="status" style="height: 12px; width: 12px;">
              <span class="visually-hidden">Loading...</span>
            </div>
            <svg v-if="!downloading" class="download-icon" width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
            </svg>
            <span class="download-text">Download</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import moment from 'moment';

export default {
  name: 'PieSummary',

  props: ['organisationID', 'isManager', 'showProduct'],

  data() {
    return {
      logo: null,
      pieReady: false,
      btnDownload: "#00A0C1",
      downloading: false,
      downloadingSummary: false,
      mandatoryOnly: false,
      hoveredItem: null,
      summaryItems: [
        { name: 'Completed', value: 0, percentage: 0, color: '#00C46B' },
        { name: 'In Progress', value: 0, percentage: 0, color: '#0077B6' },
        { name: 'Not Started', value: 0, percentage: 0, color: '#00A0C1' },
      ],
      vssWidth: 3000,
    }
  },

  computed: {
    totalRecords() {
      return this.summaryItems.reduce((acc, item) => acc + item.value, 0);
    },

    activeSlices() {
      return this.summaryItems.filter(item => item.value > 0);
    },

    singleActiveSlice() {
      return this.activeSlices.length === 1 ? this.activeSlices[0] : null;
    },

    hoveredArc() {
      if (!this.hoveredItem) return null;
      return this.summaryItems.find(item => item.name === this.hoveredItem) || null;
    },

    donutArcs() {
      if (this.totalRecords === 0 || this.activeSlices.length <= 1) {
        return [];
      }

      const radius = 58;
      const cx = 80;
      const cy = 80;
      const count = this.activeSlices.length;

      // Crisp, subtle angular gap matching Figma (12.5 deg for 3 slices, 14 deg for 2 slices)
      const gap = count === 3 ? 12.5 : 14;
      const totalGap = count * gap;
      const availableAngle = 360 - totalGap;

      // Minimum sweep angle per slice so small percentages (e.g. 2%) form a sleek curved pill
      const minAngle = 20;
      const remainingAngle = Math.max(0, availableAngle - (count * minAngle));

      // Dynamic stroke width matching Figma: highest value is sleek (11.5px), lowest is thinnest (7.5px)
      const sortedValues = [...this.activeSlices].map(s => s.value).sort((a, b) => a - b);
      const minVal = sortedValues[0];
      const maxVal = sortedValues[sortedValues.length - 1];

      const getStrokeWidth = (val) => {
        if (count === 1) return 11;
        if (maxVal === minVal) return 9.5;
        if (count === 3) {
          if (val === minVal) return 7.5;
          if (val === maxVal) return 11.5;
          return 9.5; // Middle value
        }
        // count === 2
        return val === maxVal ? 11.5 : 8;
      };

      const startOffsetAngle = -150; // Positions Completed on upper-left, In Progress on top, Not Started on bottom
      let currentAngle = startOffsetAngle;
      const arcs = [];

      for (const item of this.activeSlices) {
        const slicePercentage = item.value / this.totalRecords;
        const sweepAngle = minAngle + (slicePercentage * remainingAngle);

        const startAngle = currentAngle + (gap / 2);
        const endAngle = startAngle + sweepAngle;

        const radStart = (startAngle * Math.PI) / 180;
        const radEnd = (endAngle * Math.PI) / 180;

        const x1 = cx + radius * Math.cos(radStart);
        const y1 = cy + radius * Math.sin(radStart);
        const x2 = cx + radius * Math.cos(radEnd);
        const y2 = cy + radius * Math.sin(radEnd);

        const largeArcFlag = sweepAngle > 180 ? 1 : 0;

        const d = `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;

        arcs.push({
          name: item.name,
          value: item.value,
          percentage: item.percentage,
          color: item.color,
          strokeWidth: getStrokeWidth(item.value),
          d,
        });

        currentAngle += sweepAngle + gap;
      }

      return arcs;
    },

    orgName() {
      return this.$store.state.auth.isSuperAdmin || this.$helper.isVertexAnalyst() ? this.$store.state.user.organisation.name : this.$store.state.user.organisation.organisation
    },

    formattedOrgName() {
      if (this.orgName) {
        if (this.orgName.includes(" ")) {
          return this.orgName.replace(/ /g, "_");
        } else {
          return this.orgName;
        }
      }
      return "";
    },

    orgLogo() {
      var logoData = null

      if (!this.logo)
        return require("@/assets/img/auth-sm.png")

      if (this.logo.length != null)
        logoData = this.logo

      return logoData ? this.logo : require("@/assets/img/auth-sm.png")
    },
  },

  async created() {
    if (!this.showProduct) return

    await this.getData()
    this.vssWidth = this.$vssWidth;

    let org = await this.getOrganisation(this.organisationID)
    this.logo = org && org.organisation_logo ? org.organisation_logo.logo : null
  },

  methods: {
    async onMandatoryChange() {
      await this.getData();
    },

    getData() {
      this.pieReady = false;

      let params = {
        manager: this.isManager,
        mandatory: this.mandatoryOnly,
      }

      return new Promise((resolve, reject) => {
        this.$axios.get("/trainings/organisations/" + this.organisationID + "/answers/pie-summary", { params })
          .then(response => {
            if (response.data.success) {
              let dataMap = {
                'Completed': 0,
                'In Progress': 0,
                'Not Started': 0,
              };
              var total = 0;
              for (const summary of response.data.pie_summary) {
                total += summary.value;
                if (Object.prototype.hasOwnProperty.call(dataMap, summary.name)) {
                  dataMap[summary.name] = summary.value;
                }
              }

              this.summaryItems = [
                {
                  name: 'Completed',
                  value: dataMap['Completed'],
                  percentage: total > 0 ? (dataMap['Completed'] / total) * 100 : 0,
                  color: '#00C46B',
                },
                {
                  name: 'In Progress',
                  value: dataMap['In Progress'],
                  percentage: total > 0 ? (dataMap['In Progress'] / total) * 100 : 0,
                  color: '#0077B6',
                },
                {
                  name: 'Not Started',
                  value: dataMap['Not Started'],
                  percentage: total > 0 ? (dataMap['Not Started'] / total) * 100 : 0,
                  color: '#00A0C1',
                },
              ];

              this.pieSeries = [
                dataMap['Completed'],
                dataMap['In Progress'],
                dataMap['Not Started'],
              ];

              this.pieReady = true;
              resolve();
            }
          }).catch(error => {
            reject(error)
          })
      })
    },

    sanitizeSpreadsheetCell(val) {
      if (typeof val !== 'string') return val ?? ''
      if (/^[=+\-@\t\r]/.test(val)) {
        return `'${val}`
      }
      return val
    },

    async downloadEmployeeSummary() {
      this.downloading = true;

      if (this.downloadingSummary)
        return

      let data = await this.prepareEmployeeSummary();
      var dateToday = moment().format("YYYYMMD");

      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SheetJS");

      XLSX.writeFile(wb, dateToday + "_" + this.formattedOrgName + "_employee_report.xlsx");

      this.downloadingSummary = false
      this.downloading = false
      this.btnDownload = '#00A0C1';
    },

    async prepareEmployeeSummary() {
      let employeeSummaries = await this.getEmployeeSummary();

      let rows = [["Employee", "Email", "Category", "Program Name", "Frequency", "Training Topic", "Type", "Mandatory", "Status", "Quiz Status", "Date Assigned", "Date Completed", "Quiz Score", "# of Quiz Attempt", "Ad Hoc"]];

      for (const employeeSummary of (employeeSummaries || [])) {
        let quizDate = employeeSummary["Date Completed"]

        if (quizDate != "") {
          quizDate = moment(employeeSummary["Date Completed"]).local().format("DD/MM/yyyy")
        }

        rows.push([
          this.sanitizeSpreadsheetCell(employeeSummary["Employee"]),
          this.sanitizeSpreadsheetCell(employeeSummary["Email"]),
          this.sanitizeSpreadsheetCell(employeeSummary["Category"] || "N/A"),
          this.sanitizeSpreadsheetCell(employeeSummary["Program Name"] || "N/A"),
          this.sanitizeSpreadsheetCell(employeeSummary["Frequency"] || "N/A"),
          this.sanitizeSpreadsheetCell(employeeSummary["Training Topic"]),
          this.sanitizeSpreadsheetCell(employeeSummary["Type"]),
          employeeSummary["Mandatory"] ? "Yes" : "No",
          this.sanitizeSpreadsheetCell(employeeSummary["Status"]),
          this.sanitizeSpreadsheetCell(employeeSummary["Quiz Status"]),
          moment(employeeSummary["Date Assigned"]).local().format("DD/MM/yyyy"),
          quizDate,
          employeeSummary["Quiz Score"],
          employeeSummary["# of Quiz Attempt"],
          employeeSummary["Ad Hoc"] ? "Yes" : "",
        ]);
      }
      return rows;
    },

    async getEmployeeSummary() {
      if (!this.$store.state.user.organisation) { return; }

      let params = {
        manager: this.isManager,
      }
      if (this.mandatoryOnly) {
        params.mandatory = true;
      }
      let data = await this.$axios
        .get("/trainings/organisations/" + this.organisationID + "/answers/summary", { params })
        .then((response) => {
          return response.data.quiz_summary
        })
        .catch();

      return data;
    },

    async getOrganisation(id) {
      return await this.$axios.get(`/organisations/` + id)
        .then((response) => {
          if (response.data.success) {
            return response.data.organisation;
          } else {
            this.$toast.error(response.data.message);
            return;
          }
        })
        .catch()
    },
  },
}
</script>

<style scoped>
.widget-icon-pill {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Custom Compact Mandatory Toggle on White Surface */
.mandatory-toggle-container {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.mandatory-toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  pointer-events: none;
}

.mandatory-toggle-track {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  background-color: #cbd5e1;
  border-radius: 18px;
  transition: background-color 0.25s ease, box-shadow 0.25s ease;
  flex-shrink: 0;
}

.mandatory-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background-color: #ffffff;
  border-radius: 50%;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.mandatory-toggle-input:checked + .mandatory-toggle-track {
  background-color: #00a0c1;
}

.mandatory-toggle-input:checked + .mandatory-toggle-track .mandatory-toggle-thumb {
  transform: translateX(14px);
  background-color: #ffffff;
}

.mandatory-toggle-container:hover .mandatory-toggle-track {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.mandatory-toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.summary-legend {
  user-select: none;
}

.legend-group {
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.legend-group.legend-active {
  transform: translateX(3px);
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.legend-title {
  font-size: 14px;
  font-weight: 700;
  color: #334155;
  line-height: 1.2;
}

.legend-percentage {
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  line-height: 1.2;
  margin-top: 2px;
}

.donut-chart-container {
  min-width: 160px;
  min-height: 160px;
  position: relative;
}

.donut-svg {
  overflow: visible;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05));
}

.donut-arc-path {
  cursor: pointer;
}

.donut-hover-tooltip {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  color: #334155;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  display: flex;
  align-items: center;
}

.tooltip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 5px;
  display: inline-block;
  flex-shrink: 0;
}

.tooltip-text {
  font-size: 11px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(4px);
}

/* Figma Pill Download Button */
.btn-download-pill {
  background-color: #ffffff;
  color: #00a0c1;
  border: 1.5px solid #00a0c1;
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.btn-download-pill:hover:not(:disabled) {
  background-color: #00a0c1;
  color: #ffffff;
  border-color: #00a0c1;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.35);
}

.btn-download-pill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-download-pill .download-icon {
  margin-right: 6px;
  flex-shrink: 0;
}

.btn-download-pill .download-text {
  font-weight: 700;
  font-size: 13px;
}

p.event_desc {
  position: relative;
  top: 0%;
  left: 0px;
  width: 60%;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 25px;
  height: 75px;
  margin: 0px;
  display: table-cell;
  vertical-align: middle;
  padding: 10px;
  text-shadow:
    -1px -1px 0 #00a0c1,
    1px -1px 0 #00a0c1,
    -1px 1px 0 #00a0c1,
    1px 1px 0 #00a0c1;
}

.circle {
  border-radius: 50%;
  height: 100px;
  width: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  border: 1px solid #D3E4E8;
  object-fit: fill;
}
</style>
